import Alert from '@/components/Alert/Alert'
import UniversalWebView from '@/components/UniversalWebView'
import { UniversalWebViewRef } from '@/components/universalWebView/types'
import { getAuthToken } from '@/lib/api/authToken'
import { useSwitchTeam } from '@/lib/hooks/useSwitchTeam'
import { handleClickToPay } from '@/modules/handleClickToPay'
import { selectFormBillingAccount } from '@/redux/slices/formBillingAccountSlice'
import { fetchFormThunk } from '@/redux/slices/formSlice'
import { openPublishSheet } from '@/redux/slices/publishSheetSlice'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import { PlanCode } from '@/typings/domain'
import { getCameraPermissionsAsync } from 'expo-image-picker'
import { router } from 'expo-router'
import { usePostKeyboardEventsToWebView } from '@/lib/hooks/usePostKeyboardEventsToWebView'
import { get } from 'lodash-es'
import { Ref, useCallback, useImperativeHandle, useMemo, useRef } from 'react'

type Props = {
  formToken: string
  ref?: Ref<UniversalWebViewRef>
  onPublishSuccess?: () => void
  onDirtyChange?: (isDirty: boolean) => void
}

export default function FormBuilderWebview({ formToken, ref, onPublishSuccess, onDirtyChange }: Props) {
  const dispatch = useAppDispatch()
  const switchTeam = useSwitchTeam()
  const currentBillingAccountId = useAppSelector(state => state.currentUser.user?.billingAccount.id)
  const formBillingAccount = useAppSelector(state => selectFormBillingAccount(state, formToken))
  const isCurrentUserExternal = formBillingAccount && currentBillingAccountId !== formBillingAccount.id
  const currentUser = useAppSelector(state => state.currentUser.user)

  // Internal ref used to postMessage back to the WebView from handleMessage.
  // The forwarded ref (from FormBuilderPage) is kept in sync via useImperativeHandle.
  const webviewRef = useRef<UniversalWebViewRef>(null)

  useImperativeHandle(ref, () => ({
    reload: () => webviewRef.current?.reload(),
    postMessage: (message: string) => webviewRef.current?.postMessage(message),
    goBack: () => webviewRef.current?.goBack?.(),
    goForward: () => webviewRef.current?.goForward?.(),
  }))

  usePostKeyboardEventsToWebView(webviewRef)

  const uri = useMemo(() => {
    const token = getAuthToken()
    return `${process.env.EXPO_PUBLIC_NEXT_BASE_URL}/m/forms/${formToken}/edit?m_t=${token}`
  }, [formToken])

  const handleMessage = useCallback(
    async (data: { event: string; details: unknown }) => {
      if (data.event === 'CLICK_TO_PAY') {
        if (isCurrentUserExternal) {
          Alert.alert('提示', '共享表单的此功能需要升级套餐，请联系创建者升级。')
          return
        }
        const recommendedPlanCode = get(data.details, 'recommendedPlanCode') as PlanCode | undefined
        handleClickToPay({ recommendedPlanCode, currentUser: currentUser ?? undefined })
        return
      }

      if (data.event === 'PUBLISH_FORM') {
        const formToken = get(data.details, 'formToken') as string | undefined
        if (formToken) {
          dispatch(openPublishSheet({ formToken }))
        }
        return
      }

      if (data.event === 'UNSUPPORTED_FIELDS') {
        Alert.alert('包含不支持的字段', '该表单包含不支持的字段，请前往 PC 端编辑。')
        return
      }

      if (data.event === 'FORM_NOT_FOUND') {
        router.replace('/forms')
        return
      }

      if (data.event === 'SWITCH_TEAM') {
        const userId = get(data.details, 'userId') as string | undefined
        if (userId) {
          switchTeam(userId)
        } else {
          router.push('/profile/switchUser')
        }
        return
      }

      if (data.event === 'PUBLISH_SUCCESS') {
        onPublishSuccess?.()
        return
      }

      if (data.event === 'PUBLISH_FAILED') {
        const message = get(data.details, 'message') as string | undefined
        Alert.alert('发布失败', message || '请稍后重试')
        return
      }

      if (data.event === 'FORM_SAVED') {
        dispatch(fetchFormThunk({ formToken }))
        return
      }

      if (data.event === 'REQUEST_CAMERA_PERMISSION') {
        const { granted } = await getCameraPermissionsAsync()
        webviewRef.current?.postMessage(JSON.stringify({ event: 'CAMERA_PERMISSION_RESULT', details: { granted } }))
        return
      }

      if (data.event === 'DIRTY_STATE_CHANGED') {
        const isDirty = get(data.details, 'isDirty')
        if (typeof isDirty === 'boolean') {
          onDirtyChange?.(isDirty)
        }
        return
      }
    },
    [isCurrentUserExternal, currentUser, dispatch, switchTeam, onPublishSuccess, onDirtyChange, formToken]
  )

  return (
    <UniversalWebView
      ref={webviewRef}
      source={{ uri }}
      className="flex-1 bg-background-muted"
      allow="geolocation;camera;microphone"
      onMessage={handleMessage}
      scrollEnabled={false}
    />
  )
}
